/**
 * Visual routines ("Rotina"). The routines themselves are account data
 * (shared by every device); which steps were done today is kept on this
 * device only and starts over every day.
 */
import type { Routine } from '@shared/schemas';

/** Done step indexes per routine, for one day. */
export type RoutineProgress = Record<string, { day: string; done: number[] }>;

/** Local calendar day, e.g. "2026-11-03". */
export function dayKey(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Steps done today in `routine` (yesterday's progress does not count). */
export function doneSteps(progress: RoutineProgress, routine: Routine, now = new Date()): number[] {
  const entry = progress[routine.id];
  if (!entry || entry.day !== dayKey(now)) return [];
  return entry.done.filter((i) => Number.isInteger(i) && i >= 0 && i < routine.steps.length);
}

/** Marks step `index` done, or not done again. Returns new progress. */
export function toggleStep(progress: RoutineProgress, routine: Routine, index: number, now = new Date()): RoutineProgress {
  const done = doneSteps(progress, routine, now);
  const next = done.includes(index) ? done.filter((i) => i !== index) : [...done, index].sort((a, b) => a - b);
  return { ...progress, [routine.id]: { day: dayKey(now), done: next } };
}

export function restart(progress: RoutineProgress, routine: Routine): RoutineProgress {
  const { [routine.id]: _removed, ...rest } = progress;
  return rest;
}

/** The step to do now: the first one not done, or -1 when all are done. */
export function currentStep(stepCount: number, done: number[]): number {
  for (let i = 0; i < stepCount; i++) if (!done.includes(i)) return i;
  return -1;
}
